'use strict';

/**
 * The typed audit ACTOR — what KIND of principal acted (REPAIR-LEDGER-AND-TYPED-ID-001; owner rulings RLT-Q7, Q10,
 * Q12, Q15, Q16, Q17).
 *
 * `actorKind` names the principal TYPE, and each id-bearing kind names exactly one id family for the actor column, which
 * is what lets a typed check prove an audit row's actor id belongs to the kind it claims. An authentication seam STAMPS
 * the kind on the principal it creates (`req.user.actorKind`); the audit writers read it back.
 *
 * It lives HERE, beside `SYSTEM_ACTORS`, because every seam that stamps a kind already depends on this leaf package,
 * and a kind is a statement about an id: `system` and `anonymous` are the kinds of the sentinels, the rest are families
 * of the registry. The writer-side checks (`isActorOfKind`, `assertActorOfKind`, `resolveActorKind`) live in
 * `@puffin-core/audit`, the only place that writes a kind.
 *
 * `dev_test` is deliberately absent (RLT-Q17): it is an authentication METHOD — a dev-test token is minted for a real
 * user or a real staff member — so a dev-test row's kind is its real principal's kind.
 *
 * The chain-format migrations pin their CHECK to a FROZEN copy of this list (`@puffin-core/audit` migrations
 * `TYPED_ACTOR_KINDS_V*`); this is the live vocabulary.
 *
 * @module @puffin-core/id-generator/actorKinds
 */

const { SYSTEM_ACTORS, isSystemActor } = require('./systemActors');

/** @enum {string} */
const ACTOR_KINDS = Object.freeze({
  /** A tenant user (`usr_`). */
  USER: 'user',
  /** A tenant's customer acting for themself — portal, storefront shopper, mailbox holder (`cus_` / `cac_` / `mhs_`). */
  CUSTOMER: 'customer',
  /** A white-label partner authenticated by its API key (`ptn_`). */
  PARTNER: 'partner',
  /** Platform staff (`stf_`, the platform token's `sub`) — RLT-Q10. */
  STAFF: 'staff',
  /** An owner-console principal (`ocu_`) — RLT-Q12. */
  OWNER: 'owner',
  /** A machine credential that is not a partner — port open-API key, pos analytics key, a SCIM token. */
  API: 'api',
  /** A job, sweep or internal relay — its id is a `SYSTEM_ACTORS` sentinel. */
  SYSTEM: 'system',
  /** An audited request with no principal — its id is `SYSTEM_ACTORS.ANONYMOUS` (RLT-Q16). */
  ANONYMOUS: 'anonymous',
});

/** Every kind, in declaration order. */
const ACTOR_KIND_VALUES = Object.freeze(Object.values(ACTOR_KINDS));

/**
 * The entity types whose ids each ID-BEARING kind admits in the actor column. `system` and `anonymous` carry a
 * `SYSTEM_ACTORS` sentinel instead, so they are absent here.
 */
const ACTOR_KIND_ID_TYPES = Object.freeze({
  [ACTOR_KINDS.USER]: Object.freeze(['user']),
  // `mailboxHolderSub` — a mailbox holder signed in to port's holder portal: the session names its subscription
  // (`mhs_`), because a holder may have no customer row (RLT-Q25).
  [ACTOR_KINDS.CUSTOMER]: Object.freeze(['customer', 'customerAccount', 'mailboxHolderSub']),
  [ACTOR_KINDS.PARTNER]: Object.freeze(['partner']),
  [ACTOR_KINDS.STAFF]: Object.freeze(['staff']),
  [ACTOR_KINDS.OWNER]: Object.freeze(['ownerConsoleUser']),
  [ACTOR_KINDS.API]: Object.freeze(['portApiKey', 'analyticsApiKey', 'scimToken']),
});

/**
 * The kind a `SYSTEM_ACTORS` sentinel NAMES — a sentinel is self-typing: `anonymous` for the anonymous actor,
 * `system` for every other one.
 *
 * @param {*} actorId
 * @returns {('system'|'anonymous'|null)} null when `actorId` is not a sentinel.
 */
function kindOfSentinel(actorId) {
  if (!isSystemActor(actorId)) {
    return null;
  }
  return actorId === SYSTEM_ACTORS.ANONYMOUS ? ACTOR_KINDS.ANONYMOUS : ACTOR_KINDS.SYSTEM;
}

/*
 * The ACTOR of an authentication-step audit row — the principal the step PROVED (typed-actor C3; RLT-Q7, Q16).
 *
 * A step either proves a principal or it does not. A failed login, an unknown email, a bad or reused refresh token, an
 * invalid MFA challenge, a reset requested by email alone: nothing was proven, so the actor is the anonymous requester
 * (RLT-Q16 forbids recording an unauthenticated actor as `system`). The account such an attempt TARGETED is its subject —
 * `targetUserId` / `targetStaffId` — never its actor: a stranger guessing a password is not the account holder.
 *
 * These payloads STATE their kind because the steps run before any principal is attached to the request; the writer's
 * `stated → sentinel → principal` resolution would otherwise fall through to the request's ambient `system` and refuse a
 * real id. The anonymous actor is the `SYSTEM_ACTORS.ANONYMOUS` sentinel on BOTH seams — the writer's kind check admits
 * nothing else for kind `anonymous`. The one sink that cannot hold it yet (pos `platform_audit_log.staffId`, still
 * FK'd to `platform_staff` until typed-actor item E) maps it at its own adapter; the helper never bends to that sink.
 */

/**
 * A tenant user the step proved (a correct password, a valid MFA challenge, a verified token, a session).
 *
 * @param {string} userId
 * @returns {{userId: string, actorKind: string}}
 */
function provenUserActor(userId) {
  if (!userId) {
    throw new TypeError('provenUserActor requires the id of the user the step proved');
  }
  return { userId, actorKind: ACTOR_KINDS.USER };
}

/**
 * A tenant's customer the step proved — a storefront shopper who completed an emailed-code password reset
 * (REPAIR-LEDGER-AND-TYPED-ID-001 RLT-Q40 (b)). The customer's id rides `userId`, as every customer-kind row does
 * (the storefront's own audit writes `userId: customerId, actorKind: customer`); the stated kind is what types it.
 *
 * @param {string} customerId
 * @returns {{userId: string, actorKind: string}}
 */
function provenCustomerActor(customerId) {
  if (!customerId) {
    throw new TypeError('provenCustomerActor requires the id of the customer the step proved');
  }
  return { userId: customerId, actorKind: ACTOR_KINDS.CUSTOMER };
}

/**
 * An owner-console principal the step proved — a tenant site's owner who completed an emailed-code password reset at
 * `console.<vanity>` (OWNER-RULINGS-WALK-BUILDS owner-assign, owner ruling "Build console reset", 2026-10-08). The
 * `ocu_` id rides `userId`, as the owner-console session's own principal does (`attachOwnerConsolePrincipal`); the
 * stated kind is what types it.
 *
 * @param {string} ownerConsoleUserId
 * @returns {{userId: string, actorKind: string}}
 */
function provenOwnerActor(ownerConsoleUserId) {
  if (!ownerConsoleUserId) {
    throw new TypeError(
      'provenOwnerActor requires the id of the owner-console principal the step proved',
    );
  }
  return { userId: ownerConsoleUserId, actorKind: ACTOR_KINDS.OWNER };
}

/**
 * No principal proven on the tenant side; the account the attempt aimed at, when one resolved, is the subject.
 *
 * @param {string} [targetUserId]
 * @returns {{userId: string, actorKind: string, targetUserId?: string}}
 */
function unprovenUserActor(targetUserId) {
  const actor = { userId: SYSTEM_ACTORS.ANONYMOUS, actorKind: ACTOR_KINDS.ANONYMOUS };
  return targetUserId ? { ...actor, targetUserId } : actor;
}

/**
 * A platform staff member the step proved.
 *
 * @param {string} staffId - The platform token's `sub`.
 * @returns {{staffId: string, actorKind: string}}
 */
function provenStaffActor(staffId) {
  if (!staffId) {
    throw new TypeError('provenStaffActor requires the id of the staff member the step proved');
  }
  return { staffId, actorKind: ACTOR_KINDS.STAFF };
}

/**
 * No principal proven on the platform side.
 *
 * @param {string} [targetStaffId]
 * @returns {{staffId: string, actorKind: string, targetStaffId?: string}}
 */
function unprovenStaffActor(targetStaffId) {
  const actor = { staffId: SYSTEM_ACTORS.ANONYMOUS, actorKind: ACTOR_KINDS.ANONYMOUS };
  return targetStaffId ? { ...actor, targetStaffId } : actor;
}

/**
 * The SUBJECT of an authentication-step audit row — the account it is about. An unproven attempt names
 * the account it aimed at (`targetUserId` / `targetStaffId`); a proven step is about its own actor; an
 * attempt that resolved to no account has no subject. The anonymous sentinel is never a subject.
 *
 * @param {{userId?: string, staffId?: string, targetUserId?: string, targetStaffId?: string}} payload
 * @returns {string|null}
 */
function auditSubjectOf({ userId, staffId, targetUserId, targetStaffId } = {}) {
  const target = targetUserId ?? targetStaffId;
  if (target) {
    return target;
  }
  const actor = userId ?? staffId;
  return actor && !isSystemActor(actor) ? actor : null;
}

module.exports = {
  auditSubjectOf,
  ACTOR_KINDS,
  ACTOR_KIND_VALUES,
  ACTOR_KIND_ID_TYPES,
  kindOfSentinel,
  provenUserActor,
  unprovenUserActor,
  provenCustomerActor,
  provenOwnerActor,
  provenStaffActor,
  unprovenStaffActor,
};
